import { select } from 'd3-selection';
import { arc, pie, PieArcDatum } from 'd3-shape';

import {
  Component,
  Element,
  Event,
  EventEmitter,
  Fragment,
  h,
  Host,
  Prop,
  State,
  Watch
} from '@stencil/core';

import { CHART_TYPE, CIRCULAR_CHART_INVALID_CONFIG } from '../../constants';
import {
  DonutChart,
  IChartData,
  ICircularChart,
  PieChart,
  SemiCircleChart
} from '../../models';
import { getChartModel } from '../../utils/model-helper';

@Component({
  tag: 'uui-circular-chart',
  styleUrl: 'uui-circular-chart.scss',
  shadow: true
})
export class UuiCircularChart {
  @Element() element: HTMLElement;

  @Event({ bubbles: true, composed: true }) legendClick: EventEmitter;

  @Prop() config: ICircularChart;

  @State() tooltipDataIndex: number;

  model: PieChart | DonutChart | SemiCircleChart;
  toolTipElement: HTMLElement;
  tooltipData: IChartData;
  hasEmptyMessageSlot: boolean;

  @Watch('config')
  onConfigChanged(): void {
    this.buildModel();
    this.drawChart();
  }

  componentWillLoad(): void {
    this.hasEmptyMessageSlot = !!this.element.querySelector(
      '[slot="empty-message"]'
    );
    this.buildModel();
  }

  componentDidLoad(): void {
    this.drawChart();
  }

  buildModel(): void {
    // To handle empty or null config
    if (!this.config || Object.keys(this.config).length == 0) {
      this.config = CIRCULAR_CHART_INVALID_CONFIG as ICircularChart;
    }

    this.model = getChartModel<
      CHART_TYPE.PIE | CHART_TYPE.DONUT | CHART_TYPE.SEMI_CIRCLE
    >(this.config as ICircularChart);
  }

  // NOTE: call back for additional clickable legend payload
  emitLegendPayload(event: Event, dataIndex: number): void {
    event.preventDefault();
    event.stopPropagation();

    const payload =
      this.model.legend.callbackPayload.length > dataIndex
        ? this.model.legend.callbackPayload[dataIndex]
        : undefined;

    this.legendClick.emit(payload);
  }

  drawChart(): void {
    if (!this.model?.series?.length) {
      return;
    }

    const svgElement = select(this.element.shadowRoot.querySelector('svg'))
      .attr('width', this.model.width)
      .attr('height', this.model.height);

    svgElement.selectAll('*').remove();

    // TODO: find compataible data type; as it binds to svg attr
    const arcShape: any = arc()
      .innerRadius(this.model.innerRadius)
      .outerRadius(this.model.radius);

    const pieDataStructure = pie<number>()
      .startAngle(this.model.startAngle)
      .endAngle(this.model.endAngle)
      .sort(null)
      .value((d: number) => d)(this.model.percentageData);

    const group = svgElement
      .append('g')
      .attr(
        'transform',
        `translate(${this.model.startX}, ${this.model.startY})`
      )
      .attr('stroke', this.model.strokeColor);

    const paths = group
      .selectAll('path')
      .data(pieDataStructure)
      .join('path')
      .attr('fill', (p: PieArcDatum<number>) =>
        this.model.getColorForDataNIndex(this.model.data[p.index], p.index)
      )
      .attr('d', arcShape);

    if (this.model.enableCenterText) {
      group
        .append('text')
        .attr('dy', this.model.primaryCenterLabelDy)
        .attr('class', 'uui-dv-data-metric-medium')
        .text(this.model.primaryCenterLabel);

      if (this.model.secondaryCenterLabel) {
        group
          .append('text')
          .attr('dy', this.model.secondaryCenterLabelDy)
          .attr('class', 'uui-dv-data-classification')
          .text(this.model.secondaryCenterLabel);
      }
    }

    if (this.model.tooltip) {
      paths.on(
        'mouseover',
        (e: MouseEvent, selectedItem: PieArcDatum<number>) => {
          // Dim all arcs except the hovered one
          paths
            .filter(d => d.index !== selectedItem.index)
            .attr('style', 'opacity: 30%');

          this.toogleTooltip(selectedItem.index);
          this.updateTooltipPosition(e.x, e.y);
        }
      );

      paths.on('mousemove', (e: MouseEvent) => {
        this.updateTooltipPosition(e.x, e.y);
      });

      paths.on('mouseout', () => {
        paths.attr('style', 'opacity: 100%');

        this.toogleTooltip();
        this.updateTooltipPosition(-1000, -1000); // to displace the tooltip
      });
    }
  }

  toogleTooltip(dataIndex?: number): void {
    if (this.model.data.length > dataIndex) {
      this.tooltipData = this.model.data[dataIndex];
      this.tooltipDataIndex = dataIndex;
      this.toolTipElement.style.opacity = '1';
    } else {
      this.toolTipElement.style.opacity = '0';
      this.tooltipData = undefined;
      this.tooltipDataIndex = undefined;
    }
  }

  updateTooltipPosition(x: number, y: number): void {
    this.toolTipElement.style.left = `${x + 20}px`;
    this.toolTipElement.style.top = `${y - 12}px`;
  }

  //#region - Legend templates
  // NOTE: template functions will be reused for tooltip

  getLegendTitle(): JSX.Element {
    return (
      <div class="circular-chart-legend-title">
        <span class="uui-dv-legend-label">
          {this.model.legend?.title?.text}
        </span>

        <span class="circular-chart-legend-numeral uui-dv-legend-numeral">
          {this.model.legendTotalValue}
        </span>
      </div>
    );
  }

  getLegendGridRow(data: IChartData, dataIndex: number): JSX.Element {
    return (
      <Fragment>
        {/* -------- Legend Color Palete -------------- */}
        <span
          class="circular-chart-legend-dot uui-dv-legend-label"
          style={{
            backgroundColor: this.model.getColorForDataNIndex(data, dataIndex)
          }}
        ></span>

        {/* -------- Legend Label ------------ */}
        <span class="circular-chart-legend-label uui-dv-legend-label">
          {this.model.computedLengedLabels[dataIndex]}

          {/* Call back label */}
          {this.model.legend.enableCallback &&
            this.model.legend.callbackPayload &&
            this.model.legend.callbackPayload.length > dataIndex && (
              <span
                class="circular-chart-legend-link"
                onClick={e => this.emitLegendPayload(e, dataIndex)}
              >
                {this.model.legend.callbackPayload[dataIndex]?.label}
              </span>
            )}
        </span>

        {/* -------- Legend Values ------------ */}
        {this.model.computedLegendValues.map((v, i) => {
          return (
            <Fragment>
              <span class="circular-chart-legend-numeral uui-dv-legend-numeral">
                {v[dataIndex]}
              </span>
              {this.model.computedLegendValues.length - 1 > i && (
                <span class="circular-chart-legend-numeral uui-dv-legend-numeral">
                  {this.model.legendValueSeperator}
                </span>
              )}
            </Fragment>
          );
        })}
      </Fragment>
    );
  }

  getGridTemplateColumns(): string {
    // no of legend values [20%, 2189] + no of legend seperators + 1 (legend dot) + 1 (legend label)
    const noOfLegendValues =
      this.model.computedLegendValues.length +
      (this.model.computedLegendValues.length - 1);

    return this.model.legend?.stretchToMaxWidth
      ? `min-content auto repeat(${noOfLegendValues}, min-content)`
      : `repeat(${noOfLegendValues + 2}, auto)`;
  }

  getLegnedGrid(gridRows: JSX.Element | JSX.Element[]): JSX.Element {
    return (
      <div
        class="circular-chart-legend-grid"
        style={{ 'grid-template-columns': this.getGridTemplateColumns() }}
      >
        {gridRows}
      </div>
    );
  }

  getChartLegend(): JSX.Element {
    const wrapperClass = this.model.legend?.stretchToMaxWidth
      ? 'circular-chart-legend-wrapper-grow'
      : '';

    const rows = this.model.data.map((d, i) => {
      return this.getLegendGridRow(d, i);
    });

    return (
      <div class={wrapperClass} style={{ order: this.model.legendFlexOrder }}>
        {/* --------- Legend Title ----------- */}
        {this.model.legend.title && this.getLegendTitle()}

        {this.getLegnedGrid(rows)}
      </div>
    );
  }
  //#endregion

  //#region - Tooltip
  getChartTooltip(): JSX.Element {
    const labels = this.model.tooltip?.labels;
    
    if(labels?.length) {
      return this.getTooltipFromTooltipObj();
    }

    return this.getTooltipFromLegend();
  }

  getTooltipFromTooltipObj(): JSX.Element {
    let tooltipDescription = this.model.tooltip.values[this.tooltipDataIndex] || '';
    const maxTooltipLength = this.model.tooltip.maxTooltipLength || 200;

    tooltipDescription = tooltipDescription.length > maxTooltipLength 
                          ? `${tooltipDescription.slice(0, maxTooltipLength - 3)}...`
                          : tooltipDescription ;

    return (
      <Fragment>
        <div class="circular-chart-tooltip-width">
          {/* --------- Title ----------- */}
          <div class="circular-chart-legend-title">
            <span class="uui-dv-legend-label">{this.model.tooltip.labels[this.tooltipDataIndex] || ''}</span>
          </div>

          {/* --------- Data ----------- */}
          <div class="uui-dv-legend-label circular-chart-tooltip-description">
            {tooltipDescription}
          </div>
        </div>
      </Fragment>
    );
  }

  getTooltipFromLegend(): JSX.Element {
    const rows = this.getLegendGridRow(this.tooltipData, this.tooltipDataIndex);

    return (
      <Fragment>
        {/* --------- Title ----------- */}
        {this.model.tooltip.showTitle &&
          this.model.legend.title &&
          this.getLegendTitle()}

        {/* --------- Data ----------- */}
        {this.getLegnedGrid(rows)}
      </Fragment>
    );
  }
  //#endregion

  render() {
    const chartSectionClass = `circular-chart-wrapper ${
      this.model.showInColumn ? 'circular-chart-wrapper-column' : ''
    } ${!this.model.isShowChart ? 'circular-chart-wrapper-hidden' : ''}`;

    return (
      <Host>
        {/*--------------- Title --------- */}
        {(this.model.title || this.model.subTitle) && (
          <uui-chart-title model={this.model}>
            {this.model.title?.useSlot && (
              <slot name="title" slot="title"></slot>
            )}
            {this.model.subTitle?.useSlot && (
              <slot name="sub-title" slot="sub-title"></slot>
            )}
          </uui-chart-title>
        )}

        <section class={chartSectionClass}>
          {/* ------- chart ---------- */}
          <svg style={{ order: this.model.svgFlexOrder }}></svg>

          {/* --------- legend ------------- */}
          {this.model.isShowChart && this.model.legend && this.getChartLegend()}
        </section>

        {/* ------------ Tooltip -------------- */}
        {this.model.isShowChart && this.model.tooltip && (
          <div
            class="circular-chart-tooltip-container circular-chart-tooltip-container-pointer-left"
            ref={el => (this.toolTipElement = el)}
          >
            {(this.tooltipData || this.model.tooltip.labels?.length) && this.getChartTooltip()}
          </div>
        )}

        {!this.model.isShowChart && (
          <uui-chart-empty-message
            model={this.model}
            hasSlot={this.hasEmptyMessageSlot}
          >
            <slot name="empty-message" slot="empty-message"></slot>
          </uui-chart-empty-message>
        )}
      </Host>
    );
  }
}
